package co.maytes.standupbot.dashboard.features.settings

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateListOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import co.maytes.standupbot.dashboard.features.integrations.LoadState
import co.maytes.standupbot.dashboard.lib.api.ApiClient
import co.maytes.standupbot.dashboard.lib.api.ApiError
import co.maytes.standupbot.dashboard.lib.api.Result
import co.maytes.standupbot.dashboard.stores.rememberApiClient
import co.maytes.standupbot.shared.dto.auditlog.AuditLogEntryDto
import co.maytes.standupbot.shared.dto.auth.RoleDto
import co.maytes.standupbot.shared.dto.common.PagedResponse
import co.maytes.standupbot.shared.dto.slack.SlackDirectoryUserDto
import co.maytes.standupbot.shared.dto.workspace.CreateInviteLinkRequestDto
import co.maytes.standupbot.shared.dto.workspace.InviteCreatedDto
import co.maytes.standupbot.shared.dto.workspace.InviteLinkDto
import co.maytes.standupbot.shared.dto.workspace.InviteMemberRequestDto
import co.maytes.standupbot.shared.dto.workspace.UpdateInviteRequestDto
import co.maytes.standupbot.shared.dto.workspace.TransferOwnershipRequestDto
import co.maytes.standupbot.shared.dto.workspace.UpdateMemberRequestDto
import co.maytes.standupbot.shared.dto.workspace.RequestWorkspaceDeletionDto
import co.maytes.standupbot.shared.dto.workspace.UpdateWorkspaceRequestDto
import co.maytes.standupbot.shared.dto.workspace.WorkspaceDeletionDto
import co.maytes.standupbot.shared.dto.workspace.WorkspaceDto
import co.maytes.standupbot.shared.dto.workspace.WorkspaceMemberDto
import io.ktor.client.plugins.timeout
import io.ktor.http.HttpMethod

class SettingsStore(private val api: ApiClient) {
    var workspaceState: LoadState by mutableStateOf(LoadState.Idle)
        private set
    var workspace: WorkspaceDto? by mutableStateOf(null)
        private set

    var membersState: LoadState by mutableStateOf(LoadState.Idle)
        private set
    val members = mutableStateListOf<WorkspaceMemberDto>()

    var auditState: LoadState by mutableStateOf(LoadState.Idle)
        private set
    val auditEntries = mutableStateListOf<AuditLogEntryDto>()
    var auditNextCursor: String? by mutableStateOf(null)
        private set
    var auditHasMore: Boolean by mutableStateOf(false)
        private set

    suspend fun refreshWorkspace(workspaceId: String) {
        workspaceState = LoadState.Loading
        when (val r = api.get<WorkspaceDto>("/api/v1/workspaces/$workspaceId")) {
            is Result.Ok -> {
                workspace = r.value
                workspaceState = LoadState.Ready
            }
            is Result.Err -> workspaceState = LoadState.Error(r.error)
        }
    }

    suspend fun patchWorkspace(
        workspaceId: String,
        body: UpdateWorkspaceRequestDto,
    ): Result<WorkspaceDto, ApiError> {
        val r = api.patch<UpdateWorkspaceRequestDto, WorkspaceDto>(
            path = "/api/v1/workspaces/$workspaceId",
            body = body,
        )
        if (r is Result.Ok) {
            workspace = r.value
        }
        return r
    }

    suspend fun uninstallWorkspace(workspaceId: String): Result<Unit, ApiError> =
        api.post<Unit>("/api/v1/workspaces/$workspaceId/uninstall")

    suspend fun reinstallWorkspace(workspaceId: String): Result<Unit, ApiError> =
        api.post<Unit>("/api/v1/workspaces/$workspaceId/reinstall")

    suspend fun requestWorkspaceDeletion(
        workspaceId: String,
        confirmWorkspaceName: String,
    ): Result<WorkspaceDeletionDto, ApiError> =
        api.post<RequestWorkspaceDeletionDto, WorkspaceDeletionDto>(
            path = "/api/v1/workspaces/$workspaceId/deletion",
            body = RequestWorkspaceDeletionDto(confirmWorkspaceName),
        )

    suspend fun refreshMembers(workspaceId: String) {
        membersState = LoadState.Loading
        when (
            val r =
                api.get<List<WorkspaceMemberDto>>("/api/v1/workspaces/$workspaceId/members")
        ) {
            is Result.Ok -> {
                members.clear()
                members.addAll(r.value)
                membersState = LoadState.Ready
            }
            is Result.Err -> membersState = LoadState.Error(r.error)
        }
    }

    suspend fun inviteSlackUser(
        workspaceId: String,
        slackUserId: String,
        role: RoleDto,
    ): Result<InviteCreatedDto, ApiError> =
        api.post<InviteMemberRequestDto, InviteCreatedDto>(
            path = "/api/v1/workspaces/$workspaceId/members/invite",
            body = InviteMemberRequestDto(slackUserId = slackUserId, role = role),
        )

    suspend fun inviteByEmail(
        workspaceId: String,
        email: String,
        role: RoleDto,
    ): Result<InviteCreatedDto, ApiError> =
        api.post<InviteMemberRequestDto, InviteCreatedDto>(
            path = "/api/v1/workspaces/$workspaceId/members/invite",
            body = InviteMemberRequestDto(email = email.trim(), role = role),
        )

    suspend fun resendInvite(
        workspaceId: String,
        inviteId: String,
    ): Result<InviteCreatedDto, ApiError> =
        api.post<InviteCreatedDto>("/api/v1/workspaces/$workspaceId/members/invites/$inviteId/resend")

    suspend fun revokeInvite(
        workspaceId: String,
        inviteId: String,
    ): Result<Unit, ApiError> = api.delete<Unit>("/api/v1/workspaces/$workspaceId/members/invites/$inviteId")

    suspend fun updateInviteRole(
        workspaceId: String,
        inviteId: String,
        role: RoleDto,
    ): Result<WorkspaceMemberDto, ApiError> =
        api.patch<UpdateInviteRequestDto, WorkspaceMemberDto>(
            path = "/api/v1/workspaces/$workspaceId/members/invites/$inviteId",
            body = UpdateInviteRequestDto(role = role),
        )

    suspend fun listInviteLinks(workspaceId: String): Result<List<InviteLinkDto>, ApiError> =
        api.get<List<InviteLinkDto>>("/api/v1/workspaces/$workspaceId/members/invite-links")

    suspend fun createInviteLink(
        workspaceId: String,
        body: CreateInviteLinkRequestDto,
    ): Result<InviteLinkDto, ApiError> =
        api.post<CreateInviteLinkRequestDto, InviteLinkDto>(
            path = "/api/v1/workspaces/$workspaceId/members/invite-links",
            body = body,
        )

    suspend fun revokeInviteLink(
        workspaceId: String,
        linkId: String,
    ): Result<Unit, ApiError> = api.delete<Unit>("/api/v1/workspaces/$workspaceId/members/invite-links/$linkId")

    suspend fun listSlackUsers(workspaceId: String): Result<List<SlackDirectoryUserDto>, ApiError> =
        api.get<List<SlackDirectoryUserDto>>("/api/v1/workspaces/$workspaceId/slack/users")

    suspend fun updateMember(
        workspaceId: String,
        userId: String,
        body: UpdateMemberRequestDto,
    ): Result<WorkspaceMemberDto, ApiError> {
        val r = api.patch<UpdateMemberRequestDto, WorkspaceMemberDto>(
            path = "/api/v1/workspaces/$workspaceId/members/$userId",
            body = body,
        )
        if (r is Result.Ok) {
            val idx = members.indexOfFirst { it.userId == userId }
            if (idx >= 0) members[idx] = r.value
        }
        return r
    }

    suspend fun disableMember(
        workspaceId: String,
        userId: String,
    ): Result<Unit, ApiError> = api.post<Unit>("/api/v1/workspaces/$workspaceId/members/$userId/disable")

    suspend fun restoreMember(
        workspaceId: String,
        userId: String,
    ): Result<Unit, ApiError> = api.post<Unit>("/api/v1/workspaces/$workspaceId/members/$userId/restore")

    suspend fun eraseMember(
        workspaceId: String,
        userId: String,
    ): Result<Unit, ApiError> = api.post<Unit>("/api/v1/workspaces/$workspaceId/members/$userId/erase")

    suspend fun transferOwnership(
        workspaceId: String,
        userId: String,
        confirmWorkspaceName: String,
    ): Result<Unit, ApiError> =
        api.post<TransferOwnershipRequestDto, Unit>(
            path = "/api/v1/workspaces/$workspaceId/members/$userId/transfer-ownership",
            body = TransferOwnershipRequestDto(confirmWorkspaceName),
        )

    suspend fun refreshAudit(
        workspaceId: String,
        filter: AuditFilter = AuditFilter(),
    ) {
        auditState = LoadState.Loading
        val query = buildAuditQuery(filter, cursor = null)
        when (
            val r =
                api.get<PagedResponse<AuditLogEntryDto>>(
                    path = "/api/v1/workspaces/$workspaceId/audit-log$query",
                )
        ) {
            is Result.Ok -> {
                auditEntries.clear()
                auditEntries.addAll(r.value.items)
                auditNextCursor = r.value.pageInfo.nextCursor
                auditHasMore = r.value.pageInfo.hasMore
                auditState = LoadState.Ready
            }
            is Result.Err -> auditState = LoadState.Error(r.error)
        }
    }

    suspend fun loadMoreAudit(
        workspaceId: String,
        filter: AuditFilter,
    ): Result<Unit, ApiError> {
        val cursor = auditNextCursor ?: return Result.Ok(Unit)
        val query = buildAuditQuery(filter, cursor = cursor)
        return when (
            val r =
                api.get<PagedResponse<AuditLogEntryDto>>(
                    path = "/api/v1/workspaces/$workspaceId/audit-log$query",
                )
        ) {
            is Result.Ok -> {
                auditEntries.addAll(r.value.items)
                auditNextCursor = r.value.pageInfo.nextCursor
                auditHasMore = r.value.pageInfo.hasMore
                Result.Ok(Unit)
            }
            is Result.Err -> Result.Err(r.error)
        }
    }

    suspend fun exportWorkspaceData(workspaceId: String): Result<ByteArray, ApiError> =
        api.post<ByteArray>("/api/v1/workspaces/$workspaceId/export") {
            timeout { requestTimeoutMillis = EXPORT_TIMEOUT_MILLIS }
        }

    suspend fun exportAuditLog(
        workspaceId: String,
        filter: AuditFilter,
    ): Result<String, ApiError> = api.get<String>("/api/v1/workspaces/$workspaceId/audit-log/export${buildAuditQuery(filter, cursor = null)}")

    private fun buildAuditQuery(filter: AuditFilter, cursor: String?): String {
        val parts = mutableListOf<String>()
        if (cursor != null) parts += "offset=$cursor"
        filter.actionPrefix?.takeIf { it.isNotBlank() }?.let { parts += "actionPrefix=$it" }
        filter.actorUserId?.takeIf { it.isNotBlank() }?.let { parts += "actorUserId=$it" }
        filter.since?.takeIf { it.isNotBlank() }?.let { parts += "since=$it" }
        filter.until?.takeIf { it.isNotBlank() }?.let { parts += "until=$it" }
        return if (parts.isEmpty()) "" else "?" + parts.joinToString("&")
    }
}

data class AuditFilter(
    val actionPrefix: String? = null,
    val actorUserId: String? = null,
    val since: String? = null,
    val until: String? = null,
)

private const val EXPORT_TIMEOUT_MILLIS = 300_000L

@Composable
fun rememberSettingsStore(): SettingsStore {
    val api = rememberApiClient()
    return remember(api) { SettingsStore(api) }
}
